import { FC } from 'react';
import { Box } from 'enova-frontend-components';
import { Controller, useWatch } from 'react-hook-form';

import { OppvarmingBrukRadio } from '../oppvarmingBrukRadio';
import { VarmesentralRadio } from '../varmesentralRadio';
import { ServiceRadio } from '../serviceRadio';
import { YearNumberField } from '../../../general/components/yearNumberField';
import { AboutFormControlProps } from '../../../types';
import { JaNeiVetIkke, OppvarmingBruk, Termostatstyring, Varmesentral } from '../../../../../../utils/registerEnums';
import { validateYear } from '../../../general/components/constructionYearField';
import { Fordelingssystem } from '../fordelingssystem';
import { KjelKaminVannbarenVarme } from '../../../../../../../../types/registration/userInput';
import { validateKjel } from '../../utils';

const defaultValue = {
  bruk: OppvarmingBruk.Ukjent,
  varmesentral: Varmesentral.Ukjent,
  serviceGjennomfortSiste2Ar: JaNeiVetIkke.VetIkke
};

type ElektrokjelFieldValue = Varmesentral | Termostatstyring | undefined;

type KjelFieldName =
  | 'oppvarming.oppvarmingOlje.kjel.value'
  | 'oppvarming.oppvarmingBioenergi.kjel.value'
  | 'oppvarming.oppvarmingBioenergi.kaminMedVannbarenVarme.value'
  | 'oppvarming.oppvarmingGass.kjel.value'
  | 'oppvarming.oppvarmingElektrisk.kjel.value';

interface Props {
  name: KjelFieldName;
}

export const kjelVannbarenVarmeDefaultValue = {
  serviceGjennomfortSiste2Ar: JaNeiVetIkke.VetIkke,
  varmesentral: Varmesentral.Ukjent,
  bruk: OppvarmingBruk.Ukjent
};

export const KjelVannbarenVarme: FC<Props & AboutFormControlProps> = ({ name, control }) => {
  const showFordelingssystem =
    useWatch({
      control,
      name: `${name}.bruk`
    }) !== OppvarmingBruk.Tappevann;

  return (
    <Box display="flex" flexDirection="column" gap={3}>
      <Controller
        name={`${name}.varmesentral`}
        control={control}
        render={({ field }) => <VarmesentralRadio value={field.value} onChange={field.onChange} />}
      />
      <Controller
        name={`${name}.bruk`}
        control={control}
        render={({ field }) => <OppvarmingBrukRadio value={field.value} onChange={field.onChange} />}
      />
      {showFordelingssystem && (
        <Controller
          name={name}
          control={control}
          rules={{
            validate: (value) => {
              return validateKjel(value) || 'Du må velge varmefordeling';
            }
          }}
          render={({ field, fieldState }) => {
            const onElektrokjelChange = (
              fieldKey: keyof KjelKaminVannbarenVarme,
              fieldValue: ElektrokjelFieldValue
            ) => {
              const currentValue = field.value || defaultValue;
              return field.onChange({
                ...currentValue,
                [fieldKey]: fieldValue
              });
            };
            return (
              <Fordelingssystem
                // Pass entire object as value, Fordelingssystem manipulates multiple properties
                value={field.value}
                error={fieldState.error}
                labelKey="register.tekniskUtstyr.kamin.fordeling.label"
                informationKey="register.varmesentral.kjel.information"
                onChange={(fieldKey, val) => onElektrokjelChange(fieldKey, val)}
              />
            );
          }}
        />
      )}
      <Controller
        name={`${name}.installasjonsAr`}
        control={control}
        rules={{
          validate: validateYear
        }}
        render={({ field }) => (
          <YearNumberField
            informationKey="register.tekniskUtstyr.kjel.installasjonsAr.information"
            value={field.value}
            onChange={(year) => field.onChange(year ? Number(year) : undefined)}
          />
        )}
      />
      <Controller
        name={`${name}.serviceGjennomfortSiste2Ar`}
        control={control}
        render={({ field }) => (
          <ServiceRadio
            value={field.value}
            onChange={field.onChange}
            i18nPrefix="register.tekniskUtstyr.kjel.serviceGjennomfortSiste2Ar"
          />
        )}
      />
    </Box>
  );
};
